/**
 * IntakeTab Component
 * AIDLC Administrative Backend
 *
 * What the customer asked for. The requirements intake is run in the CRM,
 * with the customer's stakeholders; when it is approved the CRM sends it here
 * and each approved requirement becomes a backlog item with an estimate. This
 * tab shows that packet as it was received (who was asked, what they said,
 * who approved it) and, for each requirement, the estimate beside what the
 * work has cost so far. Where the intake names the systems the product must
 * connect to, and the documents the customer supplied, it lists those too:
 * what each document is, never the file. The files stay in the CRM, so there
 * is nothing here to open or download.
 *
 * It is a read-only copy. The intake is changed in the CRM, which sends it
 * again; work already started is never rewritten from there.
 */

import { useState, useEffect } from 'react';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import { getProjectIntakes } from '../../../services/intakeService';
import type { IntakeStatus, IntakeRequirementRow, IntakeConnector, IntakeDocument } from '../../../services/intakeService';

interface IntakeTabProps {
  projectId: string;
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const count = new Intl.NumberFormat('en-US');

const STATUS_LABELS: Record<string, string> = { new: 'Not started', open: 'Open', in_progress: 'In progress', review: 'In review', done: 'Done', closed: 'Closed' };
const ROLE_LABELS: Record<string, string> = { sponsor: 'Sponsor', approver: 'Approver', sme: 'Subject-matter expert', end_user: 'End user', it_security: 'IT / security', procurement: 'Procurement' };

function statusOf(row: IntakeRequirementRow): string {
  return row.withdrawn ? 'Withdrawn in the CRM' : (STATUS_LABELS[row.status] ?? row.status);
}

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-zinc-400">{label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums text-black dark:text-white">{value}</div>
      {note && <div className="text-[11px] text-gray-500 dark:text-zinc-400 mt-1">{note}</div>}
    </div>
  );
}

/** A pick-list word from the CRM as a person reads it: `hl7_v2` is "hl7 v2". Nothing is a dash. */
const said = (word: string | null): string => (word ? word.replace(/_/g, ' ') : '—');

const KILOBYTE = 1024;
function sizeOf(bytes: number): string {
  if (bytes < KILOBYTE) return `${bytes} bytes`;
  if (bytes < KILOBYTE * KILOBYTE) return `${Math.round(bytes / KILOBYTE)} KB`;
  return `${(bytes / (KILOBYTE * KILOBYTE)).toFixed(1)} MB`;
}

const FINGERPRINT_LENGTH = 12;
const DIRECTION_LABELS: Record<IntakeConnector['direction'], string> = { inbound: 'Inbound', outbound: 'Outbound', both: 'Both ways' };
const TEST_ENVIRONMENT_LABELS: Record<IntakeConnector['testEnvironment'], string> = { yes: 'Yes', no: 'No', unknown: 'Not known' };
const PATIENT_INFORMATION: Record<IntakeConnector['patientInformation'], { label: string; tone: string }> = {
  yes: { label: 'Yes', tone: 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-800' },
  not_sure: { label: 'Not sure', tone: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800' },
  no: { label: 'No', tone: 'bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700' },
};
const DOCUMENT_STATUS_LABELS: Record<Exclude<IntakeDocument['status'], 'current'>, string> = {
  superseded: 'Superseded', withdrawn: 'Withdrawn in the CRM', destroyed: 'Destroyed in the CRM',
};
/** Text that is no longer in force: a withdrawn connector, a document that was replaced or removed. */
const SET_ASIDE = 'text-zinc-500 dark:text-zinc-400';
const IN_FORCE = 'text-zinc-800 dark:text-zinc-200';
const MUTED = 'text-gray-500 dark:text-zinc-400';
/** On whatever shows a customer's own words: a title, a name or a description with no space in it for hundreds of
 *  characters breaks where it must, and stays inside its card instead of stretching it. Not put on labels and
 *  figures, which should never break inside a word. */
const FREE_TEXT = '[overflow-wrap:anywhere]';
const COLUMN_HEADING = 'text-left px-3 py-2 font-medium first:pl-4 last:pr-4';

function Marker({ children, tone }: { children: string; tone?: string }) {
  return (
    <span className={`inline-block max-w-full border rounded px-1.5 py-0.5 text-[10px] font-medium ${tone ?? 'bg-zinc-100 text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}>
      {children}
    </span>
  );
}

/** The connectors a requirement uses, by the number people know them by, saying which have been withdrawn. */
function connectorNames(refs: string[], connectors: IntakeConnector[]): string {
  return refs.map((ref) => {
    const connector = connectors.find((c) => c.externalRef === ref);
    if (!connector) return null;
    return connector.status === 'withdrawn' ? `${connector.ref} (withdrawn)` : connector.ref;
  }).filter(Boolean).join(', ');
}

function ConnectorsTable({ connectors, requirements }: { connectors: IntakeConnector[]; requirements: IntakeRequirementRow[] }) {
  const requirementRef = new Map(requirements.map((r) => [r.externalRef, r.ref]));
  const usedBy = (c: IntakeConnector): string => {
    const known = c.requirementRefs.map((ref) => requirementRef.get(ref)).filter(Boolean);
    const others = c.requirementRefs.length - known.length;
    if (c.requirementRefs.length === 0) return c.informationOnly ? 'For information only' : 'No requirement';
    return [...known, ...(others > 0 ? [`${others} not in the backlog`] : [])].join(', ');
  };
  // Six columns, so that all of them show in the card at an ordinary laptop width. Who owns a connector and whether
  // it has a test environment are said under its name. The headings set how narrow a column may get; the
  // customer's own words wrap inside whatever they are given, which for the first column is never less than twelve
  // rem: on a narrow screen the table scrolls sideways inside its card before that column is squeezed to a ribbon.
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className={`text-[10px] uppercase tracking-wider ${MUTED}`}>
            <th scope="col" className={COLUMN_HEADING}>Connector</th>
            <th scope="col" className={COLUMN_HEADING}>Direction</th>
            <th scope="col" className={COLUMN_HEADING}>Method</th>
            <th scope="col" className={COLUMN_HEADING}>Authentication</th>
            <th scope="col" className={COLUMN_HEADING}>Patient information</th>
            <th scope="col" className={COLUMN_HEADING}>Used by</th>
          </tr>
        </thead>
        <tbody>
          {connectors.map((c) => {
            const withdrawn = c.status === 'withdrawn';
            return (
              <tr key={c.externalRef} className={`border-t border-zinc-100 dark:border-zinc-800 align-top ${withdrawn ? SET_ASIDE : IN_FORCE}`}>
                <td className={`px-3 py-2 pl-4 min-w-[12rem] max-w-[22rem] ${FREE_TEXT}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`min-w-0 font-medium ${withdrawn ? 'line-through' : 'text-black dark:text-white'}`}>{c.ref} · {c.system}</span>
                    {withdrawn && <Marker>Withdrawn in the CRM</Marker>}
                    {c.informationOnly && <Marker>For information only</Marker>}
                  </div>
                  {c.purpose && <div className="mt-1 whitespace-pre-wrap">{c.purpose}</div>}
                  {c.dataKind && <div className={`mt-1 text-[11px] whitespace-pre-wrap ${MUTED}`}>Data: {c.dataKind}</div>}
                  {c.notes && <div className={`mt-1 text-[11px] whitespace-pre-wrap ${MUTED}`}>Notes: {c.notes}</div>}
                  <div className={`mt-1 text-[11px] ${MUTED}`}>Owner: {c.owner ? `${c.owner.name}${c.owner.title ? `, ${c.owner.title}` : ''}` : 'not named'}</div>
                  <div className={`text-[11px] ${MUTED}`}>Test environment: {TEST_ENVIRONMENT_LABELS[c.testEnvironment]}</div>
                </td>
                <td className="px-3 py-2">{DIRECTION_LABELS[c.direction]}</td>
                <td className={`px-3 py-2 ${FREE_TEXT}`}>
                  <div>{said(c.method)}</div>
                  {c.frequency && <div className={`text-[11px] ${MUTED}`}>{said(c.frequency)}</div>}
                </td>
                <td className={`px-3 py-2 ${FREE_TEXT}`}>{said(c.authentication)}</td>
                <td className="px-3 py-2"><Marker tone={PATIENT_INFORMATION[c.patientInformation].tone}>{PATIENT_INFORMATION[c.patientInformation].label}</Marker></td>
                <td className={`px-3 py-2 pr-4 ${FREE_TEXT}`}>{usedBy(c)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DocumentsList({ documents, connectors, requirements, notice }: { documents: IntakeDocument[]; connectors: IntakeConnector[]; requirements: IntakeRequirementRow[]; notice: string }) {
  const numberOf = new Map<string, string>([
    ...requirements.map((r): [string, string] => [r.externalRef, r.ref]),
    ...connectors.map((c): [string, string] => [c.externalRef, c.ref]),
    ...documents.map((d): [string, string] => [d.externalRef, d.ref]),
  ]);
  return (
    <>
      <div role="note" className="mx-4 mt-3 border rounded px-3 py-2 text-xs bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-100">
        {/* The portal's own sentence, as the server sends it with the intake (documentNotice): once, above them all. It is never text from a packet. */}
        <p>{notice}</p>
      </div>
      <ul className="text-xs">
        {documents.map((d, index) => {
          const current = d.status === 'current';
          const successor = documents.find((other) => other.replacesRef === d.externalRef);
          const relatesTo = [...d.requirementRefs, ...d.connectorRefs].map((ref) => numberOf.get(ref)).filter(Boolean).join(', ');
          return (
            <li key={d.externalRef} className={`px-4 py-3 min-w-0 ${FREE_TEXT} ${index > 0 ? 'border-t border-zinc-100 dark:border-zinc-800' : ''} ${current ? IN_FORCE : SET_ASIDE}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`min-w-0 font-medium ${current ? 'text-black dark:text-white' : 'line-through'}`}>{d.ref} · {d.title || 'Title removed'}</span>
                {d.status !== 'current' && <Marker>{`${DOCUMENT_STATUS_LABELS[d.status]}${successor ? ` by ${successor.ref}` : ''}`}</Marker>}
              </div>
              {d.description && <div className="mt-1 whitespace-pre-wrap">{d.description}</div>}
              <div className={`mt-1 text-[11px] ${MUTED}`}>
                {d.type} · {sizeOf(d.sizeBytes)} · fingerprint <span className="font-mono">{d.sha256.slice(0, FINGERPRINT_LENGTH)}</span>
                {relatesTo && <> · relates to {relatesTo}</>}
                {d.replacesRef && numberOf.get(d.replacesRef) && <> · replaces {numberOf.get(d.replacesRef)}</>}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function RequirementsTable({ rows, connectors }: { rows: IntakeRequirementRow[]; connectors: IntakeConnector[] }) {
  if (rows.length === 0) {
    return <div className="px-4 py-6 text-xs text-gray-500 dark:text-zinc-400">No approved requirement has been sent for this intake yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-zinc-400">
            <th scope="col" className="text-left px-4 py-2 font-medium">Requirement</th>
            <th scope="col" className="text-left px-4 py-2 font-medium">Status</th>
            <th scope="col" className="text-left px-4 py-2 font-medium">Agent</th>
            <th scope="col" className="text-right px-4 py-2 font-medium">Est. tokens</th>
            <th scope="col" className="text-right px-4 py-2 font-medium">Est. cost</th>
            <th scope="col" className="text-right px-4 py-2 font-medium">Actual tokens</th>
            <th scope="col" className="text-right px-4 py-2 font-medium">Actual cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.itemId} className={`border-t border-zinc-100 dark:border-zinc-800 ${row.withdrawn ? SET_ASIDE : IN_FORCE}`}>
              <td className="px-4 py-2 min-w-[12rem]">
                <div className={`font-medium text-black dark:text-white ${FREE_TEXT}`}>{row.title}</div>
                <div className="text-[11px] text-gray-500 dark:text-zinc-400">{row.itemId}</div>
                {row.connectorRefs && row.connectorRefs.length > 0 && (
                  <div className={`text-[11px] ${MUTED}`}>Connects to {connectorNames(row.connectorRefs, connectors)}</div>
                )}
              </td>
              <td className="px-4 py-2">{statusOf(row)}</td>
              <td className="px-4 py-2">{row.assignedAgent ?? '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">{row.estimate ? count.format(row.estimate.inputTokens + row.estimate.outputTokens) : '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">{row.estimate ? usd.format(row.estimate.estimatedCost) : '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">{count.format(row.actual.totalTokens)}</td>
              <td className="px-4 py-2 text-right tabular-nums font-medium">{usd.format(row.actual.costUsd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IntakeCard({ status }: { status: IntakeStatus }) {
  const { intake, requirements, estimated, actual } = status;
  const live = requirements.filter((r) => !r.withdrawn);
  const done = live.filter((r) => r.status === 'done' || r.status === 'closed').length;
  const connectors = intake.connectors ?? [];
  const documents = intake.documents ?? [];
  const activeConnectors = connectors.filter((c) => c.status === 'active').length;
  const currentDocuments = documents.filter((d) => d.status === 'current').length;
  return (
    <section className="space-y-4" aria-label={`Intake ${intake.number}`}>
      <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className={`min-w-0 text-sm font-semibold text-black dark:text-white ${FREE_TEXT}`}>{intake.number} · {intake.title}</h3>
          <span className="text-[11px] text-gray-500 dark:text-zinc-400">Revision {intake.revision} · received {new Date(intake.updatedAt).toLocaleString()}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs mt-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-zinc-400">Customer</div>
            <div className="mt-1 font-medium text-black dark:text-white">{intake.customerName}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-zinc-400">Approved by</div>
            <div className="mt-1 font-medium text-black dark:text-white">{intake.approvedBy ?? 'Not recorded'}</div>
            {intake.approvedOn && <div className="text-[11px] text-gray-500 dark:text-zinc-400">{intake.approvedOn}</div>}
          </div>
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-zinc-400">Stakeholders</div>
            <div className="mt-1 text-black dark:text-white">
              {intake.stakeholders.length === 0 ? '—' : intake.stakeholders.map((s) => `${s.name}${s.role ? ` (${ROLE_LABELS[s.role] ?? s.role})` : ''}`).join(', ')}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Figure label="Requirements" value={count.format(live.length)} note={`${count.format(done)} done${requirements.length > live.length ? ` · ${requirements.length - live.length} withdrawn` : ''}`} />
        <Figure label="Estimated tokens" value={count.format(estimated.totalTokens)} note={`${count.format(estimated.requirements)} estimated`} />
        <Figure label="Estimated cost" value={usd.format(estimated.costUsd)} note="At the configured model rates" />
        <Figure label="Actual cost so far" value={usd.format(actual.costUsd)} note={`${count.format(actual.sessions)} agent sessions on these requirements`} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
        <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 text-xs font-semibold text-black dark:text-white">Requirements: estimate against actual</div>
        <RequirementsTable rows={requirements} connectors={connectors} />
      </div>

      {connectors.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
          <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 text-xs font-semibold text-black dark:text-white">
            Connectors <span className={`font-normal ${MUTED}`}>· {count.format(activeConnectors)} in use{connectors.length > activeConnectors ? ` · ${connectors.length - activeConnectors} withdrawn` : ''}</span>
          </div>
          <ConnectorsTable connectors={connectors} requirements={requirements} />
        </div>
      )}

      {documents.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
          <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 text-xs font-semibold text-black dark:text-white">
            Supporting documents <span className={`font-normal ${MUTED}`}>· {count.format(currentDocuments)} current of {count.format(documents.length)} · held in the CRM</span>
          </div>
          <DocumentsList documents={documents} connectors={connectors} requirements={requirements} notice={status.documentNotice ?? ''} />
        </div>
      )}

      {intake.sections.map((section) => (
        <details key={section.key} className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
          <summary className="px-4 py-3 text-xs font-semibold text-black dark:text-white cursor-pointer">
            {section.title} <span className="font-normal text-gray-500 dark:text-zinc-400">· {section.answers.filter((a) => a.answer).length} of {section.answers.length} answered</span>
          </summary>
          <dl className="px-4 pb-4 space-y-3 text-xs">
            {section.answers.map((a) => (
              <div key={a.key}>
                <dt className="text-gray-500 dark:text-zinc-400">{a.question}</dt>
                <dd className={`mt-1 text-black dark:text-white whitespace-pre-wrap ${FREE_TEXT}`}>{a.answer || <span className={MUTED}>Not answered</span>}</dd>
                {a.answer && a.answeredBy && <dd className="text-[11px] text-gray-500 dark:text-zinc-400">Answered by {a.answeredBy}</dd>}
              </div>
            ))}
          </dl>
        </details>
      ))}
    </section>
  );
}

export function IntakeTab({ projectId }: IntakeTabProps) {
  const [intakes, setIntakes] = useState<IntakeStatus[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getProjectIntakes(projectId);
        if (!cancelled) setIntakes(data);
      } catch (err) {
        console.error('[IntakeTab] Failed to load intakes:', err);
        if (!cancelled) setError('The intake could not be loaded. Try again in a moment.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  if (isLoading) return <LoadingSpinner />;
  if (error || !intakes) {
    return <EmptyState title="Intake unavailable" description={error ?? 'Nothing came back for this project.'} />;
  }
  if (intakes.length === 0) {
    return (
      <EmptyState
        title="No requirements intake"
        description="No intake has been sent from the CRM for this project. An intake is run in the CRM with the customer's stakeholders; once it is approved there, its requirements arrive here as backlog items with estimates."
      />
    );
  }

  return (
    <div className="space-y-8">
      {intakes.map((status) => <IntakeCard key={status.intake.externalRef} status={status} />)}
      {/* On the page itself, not on a card: the page stays light in both colour schemes, so this text stays dark in both. */}
      <div className="text-[11px] text-zinc-600">
        This is a copy of what the CRM sent. To change a requirement, change it in the CRM and submit the intake again: an item nobody has started follows the change, and work already in hand is left as it is.
      </div>
    </div>
  );
}
